import React, { useMemo, useRef, useEffect } from "react";
import { Mesh, Group, MeshBasicMaterial } from "three";
import anime from "animejs";
import * as meshline from "three.meshline";
import { flatMap, range } from "lodash";
import { useRender, extend } from "react-three-fiber";
import {
  ANALYSER_NUM_SPECTRAL_FREQUENCIES,
  PROGRESS_INDICATOR_RADIUS,
  INDICATOR_SHRINK_DURATION,
  MAX_SOUND_DURATION
} from "../../constants";
import { State } from "../../core";
import { analyser } from "../../audio";
import { AudioVisualiserUI } from "./AudioVisualiserUI";

extend(meshline);

const INNER_RADIUS = 0.7;
const CIRCLE_RADIUS = 3.0;
const RING_DEPTH = CIRCLE_RADIUS - INNER_RADIUS;
const VOL_STEPS = 60;

const PROGRESS_WAVEFORM_STEPS = 150;
const PROGRESS_STEP_ANGLE = (Math.PI * 2) / PROGRESS_WAVEFORM_STEPS;

const PROGRESS_RECORDED_COLOR = "#CCCCFF";
const PROGRESS_PLAYED_COLOR = "#9999FF";

interface AudioVisualizerProps {
  state: State;
}
interface ScaleStats {
  sampleSum: number;
  sampleCount: number;
}
export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ state }) => {
  let volumeQueue = useMemo(
    () => ({ currentStep: VOL_STEPS - 1, steps: new Float32Array(VOL_STEPS) }),
    []
  );
  let volBuffer = useMemo(
    () => new Uint8Array(ANALYSER_NUM_SPECTRAL_FREQUENCIES),
    []
  );
  let coords = useMemo(
    () =>
      flatMap(
        range(-CIRCLE_RADIUS, CIRCLE_RADIUS + 0.1, CIRCLE_RADIUS / 10),
        x =>
          range(-CIRCLE_RADIUS, CIRCLE_RADIUS + 0.1, CIRCLE_RADIUS / 10).map(
            y => ({
              x,
              y,
              dist: Math.sqrt(x ** 2 + y ** 2),
              volIdx: Math.round(
                ((Math.sqrt(x ** 2 + y ** 2) - INNER_RADIUS) / RING_DEPTH) *
                  VOL_STEPS
              )
            })
          )
      ).filter(itm => itm.dist < CIRCLE_RADIUS && itm.dist > INNER_RADIUS),
    []
  );
  let meshRefs = useRef(range(21 ** 2).map(() => React.createRef<Mesh>()));

  let progressGroupRef = useRef<Group>();
  let progressMeshRefs = useRef(
    range(PROGRESS_WAVEFORM_STEPS).map(() => React.createRef<Mesh>())
  );
  let progressMeshScales = useMemo<ScaleStats[]>(
    () =>
      range(PROGRESS_WAVEFORM_STEPS).map(() => ({
        sampleSum: 0,
        sampleCount: 0
      })),
    []
  );

  useEffect(
    function progressScaler() {
      if (state.state === "sending") {
        anime({
          targets: (progressGroupRef.current as Group).scale,
          y: 0.01,
          x: 0.01,
          z: 0.01,
          duration: INDICATOR_SHRINK_DURATION,
          easing: "easeInElastic(10, 1)"
        });
      } else if (state.state === "sent" || state.state === "sendingFailed") {
        anime({
          targets: (progressGroupRef.current as Group).scale,
          y: 1,
          x: 1,
          z: 1,
          duration: INDICATOR_SHRINK_DURATION,
          easing: "easeOutCubic"
        });
      }
    },
    [state]
  );

  useRender(
    () => {
      let progressVol = 0,
        stepVol = 0;
      if (
        state.state === "recording" ||
        state.state === "pending" ||
        state.state === "sent"
      ) {
        analyser.getByteTimeDomainData(volBuffer);
        let avg = getAverageVolume(volBuffer);
        progressVol = avg;
        if (!isFinite(avg)) avg = -100;
        avg += 100;
        avg -= 60;
        if (avg < 0) {
          avg = 0;
        }
        stepVol = -avg / 70;
      }
      volumeQueue.steps[volumeQueue.currentStep] = stepVol;
      for (let i = 0; i < coords.length; i++) {
        let mesh = meshRefs.current[i].current as Mesh;
        let vol =
          volumeQueue.steps[
            (coords[i].volIdx + volumeQueue.currentStep) % VOL_STEPS
          ];
        mesh.position.z = vol;
      }

      volumeQueue.currentStep -= 1;
      if (volumeQueue.currentStep < 0) {
        volumeQueue.currentStep += VOL_STEPS;
      }

      // Progress stuff
      if (state.state === "recording") {
        let recordProgress =
          state.recorder.recordedDuration / MAX_SOUND_DURATION;
        let currentSegment = Math.min(
          Math.floor(recordProgress * PROGRESS_WAVEFORM_STEPS),
          PROGRESS_WAVEFORM_STEPS - 1
        );
        if (!isFinite(progressVol)) progressVol = -100;
        let scale = 1 + ((100 - -progressVol) / 100) * 2;
        progressMeshScales[currentSegment].sampleSum += scale;
        progressMeshScales[currentSegment].sampleCount += 1;
        for (let i = 0; i < PROGRESS_WAVEFORM_STEPS; i++) {
          let stats = progressMeshScales[i];
          let mean =
            stats.sampleCount > 0 ? stats.sampleSum / stats.sampleCount : 0.01;
          (progressMeshRefs.current[i].current as Mesh).scale.setScalar(mean);
          ((progressMeshRefs.current[i].current as Mesh)
            .material as MeshBasicMaterial).color.setStyle(
            PROGRESS_RECORDED_COLOR
          );
        }
      } else if (
        state.state === "pending" ||
        state.state === "sending" ||
        state.state === "sent"
      ) {
        let playbackProgress = state.player.playedDuration / MAX_SOUND_DURATION;
        let currentSegment = Math.min(
          Math.floor(playbackProgress * PROGRESS_WAVEFORM_STEPS),
          PROGRESS_WAVEFORM_STEPS - 1
        );
        for (let i = 0; i < PROGRESS_WAVEFORM_STEPS; i++) {
          let color =
            i <= currentSegment
              ? PROGRESS_PLAYED_COLOR
              : PROGRESS_RECORDED_COLOR;
          let mesh = progressMeshRefs.current[i].current as Mesh;
          let material = mesh.material as MeshBasicMaterial;
          material.color.setStyle(color);
        }
      } else {
        for (let i = 0; i < PROGRESS_WAVEFORM_STEPS; i++) {
          progressMeshScales[i] = { sampleSum: 0, sampleCount: 0 };
          (progressMeshRefs.current[i].current as Mesh).scale.setScalar(0.01);
        }
      }
    },
    false,
    [volumeQueue, state]
  );

  return (
    <AudioVisualiserUI
      circleRadius={CIRCLE_RADIUS}
      progressIndicatorRadius={PROGRESS_INDICATOR_RADIUS}
      progressWaveformSteps={PROGRESS_WAVEFORM_STEPS}
      progressStepAngle={PROGRESS_STEP_ANGLE}
      gridCoords={coords}
      progressGroupRef={progressGroupRef}
      meshRefs={meshRefs.current}
      progressMeshRefs={progressMeshRefs.current}
    />
  );
};

function getAverageVolume(array: Uint8Array) {
  let sumOfSquares = 0;
  for (let i = 0; i < array.length; i++) {
    let fl = (array[i] - 128) * 0.0078125;
    sumOfSquares += fl ** 2;
  }
  return 10 * Math.log10(sumOfSquares / array.length);
}
